import { useState, useRef, useCallback } from "react";
import { MoveHorizontal, X, Quote } from "lucide-react";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import { Glossary } from "../Glossary";
import { useLanguage } from "../../context/LanguageContext";
import { useHome } from "../../translations/home";
import { DemoCTA } from "./DemoCTA";

const HomeContrast = () => {
  const { language } = useLanguage();
  const c = useHome(language).contrast;
  const [pos, setPos] = useState(48);
  const { ref: afterRef, isRevealed: afterIn } = useScrollReveal({ threshold: 0.25 });
  const boxRef = useRef(null);
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos(Math.min(72, Math.max(28, ((clientX - rect.left) / rect.width) * 100)));
  }, []);

  return (
    <section className="bg-white py-20 lg:py-24" data-testid="before-after-section">
      <Glossary><div className="wrap-cayus">
        <div className="rule-cobalt mb-5" />
        <p className="mono-cayus text-cobalt-600 mb-4">{c.eyebrow}</p>
        <h2 className="font-bold text-3xl sm:text-4xl text-inkc-800 tracking-[-0.03em] mb-10 max-w-[30ch]">{c.title}</h2>

        <div
          ref={boxRef}
          className="relative rounded-xl border border-linec-300 bg-surfacec-50 overflow-hidden select-none"
          onPointerMove={(e) => dragging.current && move(e.clientX)}
          onPointerUp={() => (dragging.current = false)}
          onPointerLeave={() => (dragging.current = false)}
          data-testid="before-after-box"
        >
          <div className="ba-grid grid" style={{ gridTemplateColumns: `${pos}% ${100 - pos}%` }}>
            <div className="p-6 sm:p-9 min-w-0 border-b sm:border-b-0 sm:border-r border-linec-300">
              <span className="inline-block mono-cayus text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-2.5 py-1 mb-6">
                {c.beforeLabel}
              </span>
              <div className="flex flex-col gap-4">
                {c.before.map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5" data-testid={`contrast-before-${i}`}>
                    <X className="w-4 h-4 text-[#EF4444] flex-shrink-0 mt-1" />
                    <span className="text-[15px] text-inkc-500">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div ref={afterRef} className={`p-6 sm:p-9 min-w-0 bg-white ${afterIn ? "is-in" : ""}`}>
              <span className="inline-block mono-cayus text-[#047857] bg-[#ECFDF5] border border-[#A7F3D0] rounded-md px-2.5 py-1 mb-6">
                {c.afterLabel}
              </span>
              <div className="flex flex-col gap-4">
                {c.after.map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5" data-testid={`contrast-after-${i}`}>
                    <svg className={`check-draw check-${i + 1} w-4 h-4 flex-shrink-0 mt-1`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M4 12.5l5 5L20 6.5" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-[15px] font-medium text-inkc-800">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className="absolute top-0 bottom-0 -ml-5 w-10 hidden sm:flex items-center justify-center ba-handle"
            style={{ left: `${pos}%` }}
            onPointerDown={(e) => {
              dragging.current = true;
              e.currentTarget.setPointerCapture?.(e.pointerId);
              move(e.clientX);
            }}
            data-testid="before-after-handle"
          >
            <div className="absolute top-0 bottom-0 w-[2px] bg-cobalt-600" />
            <span className="relative w-10 h-10 rounded-full bg-cobalt-600 text-white flex items-center justify-center shadow-[0_8px_20px_-6px_rgba(0,71,171,0.65)]">
              <MoveHorizontal className="w-5 h-5" />
            </span>
          </div>
        </div>

        <figure
          className="mt-14 mb-12 mx-auto max-w-[800px] relative bg-[#F4F7FB] border border-linec-200 rounded-2xl px-8 py-10 sm:px-12 text-center"
          data-testid="home-testimonial"
        >
          <Quote className="w-14 h-14 text-cobalt-100 absolute left-6 top-5" aria-hidden="true" />
          <Quote className="w-14 h-14 text-cobalt-100 absolute right-6 bottom-5 rotate-180" aria-hidden="true" />
          <blockquote className="relative pull-quote text-xl sm:text-2xl text-inkc-800 leading-snug mb-5">« {c.quote} »</blockquote>
          <figcaption className="relative mono-cayus text-inkc-400">{c.author}</figcaption>
        </figure>

        <DemoCTA from="contrast" variant="ghost" />
      </div></Glossary>
    </section>
  );
};

export default HomeContrast;
